// The HSE / Local Authority enforcement notices and prosecutions register (Session AON), CHAS Q94's
// companion to the accident register (AccidentsPage.tsx). Office/admin. A filterable list with a
// "Record a notice" modal; a row opens the detail page.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { api, ApiError, type EnforcementActionListFilters, type EnforcementActionListItem, type EnforcementActionStatus, type EnforcementActionType } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { ACTION_TYPES, ACTION_TYPE_LABEL } from "./enforcementActionsUi";

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "remedied", label: "Remedied" },
  { value: "closed", label: "Closed" },
];

// A client-side search and sort over the rows the (unchanged) server status filter returned. The
// search term lives only in the URL's ?q=. It matches only what the table shows.
const ENFORCEMENT_SEARCH: SearchAccessors<EnforcementActionListItem> = [
  (r) => r.ref,
  (r) => ACTION_TYPE_LABEL[r.actionType] ?? r.actionType,
  (r) => r.issuingBody,
];
const STATUS_ORDER: Record<string, number> = { open: 0, remedied: 1, closed: 2 };
const ENFORCEMENT_SORT: SortAccessors<EnforcementActionListItem> = {
  ref: (r) => r.ref,
  type: (r) => ACTION_TYPE_LABEL[r.actionType] ?? r.actionType,
  issuedBy: (r) => (r.issuingBody ?? "").toLowerCase(),
  date: (r) => {
    const t = Date.parse(r.dateIssued);
    return Number.isNaN(t) ? null : t;
  },
  status: (r) => STATUS_ORDER[r.status] ?? null,
  document: (r) => (r.hasDocument ? 1 : 0),
};
const SORT_OPTIONS = [
  { key: "ref", label: "Reference" },
  { key: "type", label: "Type" },
  { key: "issuedBy", label: "Issued by" },
  { key: "date", label: "Date issued" },
  { key: "status", label: "Status" },
];

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function EnforcementActionsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<EnforcementActionListItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [adding, setAdding] = useState(false);
  // Below md the register renders as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    const filters: EnforcementActionListFilters = {};
    if (status) filters.status = status as EnforcementActionStatus;
    api.enforcementActions
      .list(filters)
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("enforcement-actions");
  const shown = rows ? sortRows(filterRows(rows, search, ENFORCEMENT_SEARCH), ENFORCEMENT_SORT, sort) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        title="Enforcement notices and prosecutions"
        className="mb-4"
        subtitle="Enforcement notices and prosecutions served by the HSE or a Local Authority, and the remedial action taken to prevent a reoccurrence."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            Record a notice
          </Button>
        }
      />

      {/* The status filter is always shown: it drives the server query, so hiding it when a filter
          returns nothing would strand the office on an empty register. */}
      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reference, type or issuing body..."
        searchLabel="Search enforcement notices"
        resultCount={rows ? { shown: shown?.length ?? 0, total: rows.length, noun: "notices" } : undefined}
        sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
        onClear={() => setSearch("")}
        filters={
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                onClick={() => setStatus(f.value)}
                className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                  status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the enforcement notices register. Please try again.
        </p>
      ) : rows === null || shown === null ? (
        <Loader label="Loading the enforcement notices register..." />
      ) : rows.length === 0 ? (
        /* The empty state is the one the Director will see for a long time, and it should read as a
           clean record backed by a real system rather than as an unpopulated form. It also names the
           CHAS answer this register exists to back up. */
        <EmptyState
          message="No enforcement notices or prosecutions on record."
          hint="That is the answer to give an assessor asking about enforcement notices or prosecutions in the last five years. Record one here the moment the HSE or a Local Authority serves it."
        />
      ) : shown.length === 0 ? (
        <EmptyState role="status" message={`No enforcement notices match "${search.trim()}".`} />
      ) : mobileCards ? (
        /* Below md the table's columns cannot share 375px. Each notice is a card: the reference
           never truncates, and the issuing body and the filed document the table hid come back. */
        <MobileCardList label="Enforcement notices">
          {shown.map((r) => (
            <MobileListCard
              key={r.id}
              to={`/office/enforcement-actions/${r.id}`}
              primary={r.ref}
              secondary={
                <>
                  {ACTION_TYPE_LABEL[r.actionType] ?? r.actionType}
                  {r.issuingBody && <span className="block text-xs opacity-70">{r.issuingBody}</span>}
                </>
              }
              meta={
                <>
                  <span className="block">{formatDate(r.dateIssued)}</span>
                  {r.hasDocument && <span className="block">Document filed</span>}
                </>
              }
              right={<StatusPill domain="enforcement" status={r.status} />}
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "18%" }} />
              <col className="max-md:hidden" style={{ width: "24%" }} />
              <col style={{ width: "14%" }} />
              <col className="cell-actions" style={{ width: "14%" }} />
              <col className="max-md:hidden cell-actions" style={{ width: "12%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Issued by" colKey="issuedBy" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Date issued" colKey="date" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Document" colKey="document" sort={sort} onToggle={sort.toggle} className="max-md:hidden cell-actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/office/enforcement-actions/${r.id}`)}
                  className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface"
                >
                  <td className="px-3 py-2 font-semibold text-ele-strong">{r.ref}</td>
                  <td className="px-3 py-2 text-ele-body">{ACTION_TYPE_LABEL[r.actionType] ?? r.actionType}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{r.issuingBody}</td>
                  <td className="px-3 py-2 text-ele-body">{formatDate(r.dateIssued)}</td>
                  <td className="cell-actions px-3 py-2">
                    <StatusPill domain="enforcement" status={r.status} />
                  </td>
                  <td className="max-md:hidden cell-actions px-3 py-2 text-xs text-ele-body">{r.hasDocument ? "Filed" : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddEnforcementActionModal
          onClose={() => setAdding(false)}
          onAdded={(id) => {
            setAdding(false);
            navigate(`/office/enforcement-actions/${id}`);
          }}
        />
      )}
    </div>
  );
}

// The create form. It asks for the facts and, optionally, the remedial action if already known --
// CHAS's own second question, but nothing forces it up front since it usually lands later.
function AddEnforcementActionModal({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  const [form, setForm] = useState({
    actionType: "improvement_notice" as EnforcementActionType,
    issuingBody: "",
    dateIssued: todayDateInput(),
    description: "",
    remedialAction: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set =
    (name: keyof typeof form) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.issuingBody.trim()) {
      setErr("Enter who issued the notice.");
      return;
    }
    if (!form.description.trim()) {
      setErr("Describe what the notice or prosecution concerned.");
      return;
    }
    if (!form.dateIssued) {
      setErr("Enter the date it was issued.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.enforcementActions.create({
        actionType: form.actionType,
        issuingBody: form.issuingBody.trim(),
        dateIssued: form.dateIssued,
        description: form.description.trim(),
        remedialAction: form.remedialAction.trim() || null,
      });
      onAdded(created.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the record. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Record an enforcement notice or prosecution" onClose={() => !busy && onClose()} width="max-w-2xl" margin="my-8" panelClassName="p-6">
      <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-strong">Record an enforcement notice or prosecution</h2>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={labelCls}>Type</span>
          <select aria-label="Type" value={form.actionType} onChange={set("actionType")} className={`${inputCls} w-full`}>
            {ACTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {ACTION_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Date issued</span>
          <input aria-label="Date issued" type="date" value={form.dateIssued} onChange={set("dateIssued")} className={`${inputCls} w-full`} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Issued by</span>
          <input
            aria-label="Issued by"
            value={form.issuingBody}
            onChange={set("issuingBody")}
            placeholder="e.g. Health and Safety Executive, or the specific local authority"
            className={`${inputCls} w-full`}
          />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>What it concerned</span>
          <textarea aria-label="What it concerned" value={form.description} onChange={set("description")} rows={3} className={`${inputCls} w-full`} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Remedial action (if already known)</span>
          <textarea
            aria-label="Remedial action"
            value={form.remedialAction}
            onChange={set("remedialAction")}
            rows={2}
            placeholder="What was done to prevent a reoccurrence. Can be added later."
            className={`${inputCls} w-full`}
          />
        </label>

        {err && (
          <p role="alert" className="sm:col-span-2 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
            {err}
          </p>
        )}
        <div className={`sm:col-span-2 mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Record it"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// Today's date, as the value a <input type="date"> wants. The office is almost always recording a
// notice that has just arrived, so the field is pre-filled rather than blank.
function todayDateInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
